import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, glyphs, useMediaQuery } from "../../../Styles";
import { ProgressSteps, NewModal, MainContentPaddingTypes, ReportModalLvl1, ReportModalLvl2, ReportModalLvl3, WaitAnimation } from "../..";
import { ReportData, ReportTemplate } from "../../../Definitions";
import { cloneDeep } from "lodash";
import { useAppSelector } from "../../../Store";
import { Graph_QL } from "../../../Services";
import { useReport } from "../../../Services/Agrisys_GqlDataCaller/ReportDataCalls/useReport";
import { useNavigate } from "react-router";
import { NavigationFunction as RootNavigation, Pages } from "../../../Navigation/root";
import NewButton from "../../BaseComponents/newButton";

interface TemplateModal_Props {
  isModalOpen: boolean
  closeModal: () => void,
  report?: ReportData
  readonly template?: ReportTemplate 
}

export default function ReportModal({ isModalOpen, closeModal, report, template }: TemplateModal_Props) {
  const navigator = useNavigate();
  const {t} = useTranslation()
  const [currentTemplateLevel, setCurrentTemplateLevel] = useState(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [reportData, setReportData] = useState<ReportData|undefined>({name: undefined, archived: undefined, startDate: undefined, endDate: undefined, dataLevel: undefined, filterParameter: [[]]})
  // const [templateData, setTemplateData] = useState<ReportTemplate|undefined>({name: undefined, dataLevel: undefined, filterParameter: [[]]})
  const mobileXs = useMediaQuery(`(max-width: 805px)`);

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);  

  const {maintainEntries: maintainReport} = useReport(connection);

  useEffect(() => {
    if (isModalOpen && template) {
      setReportData(cloneDeep({...template, archived: "shouldArchive" in template && typeof template.shouldArchive === "boolean" ? template.shouldArchive : undefined}));
      // setTemplateData({
      //   name: template.name
      // })
    }
    setCurrentTemplateLevel(1);
  }, [isModalOpen, template])
  

  // Handle template level
  const TemplateLevel = (level: 0 | 1 | 2 | 3) => {
    setCurrentTemplateLevel(level);
  };
  
  // Handle "go back"
  const onGoBack = () => {
    if (currentTemplateLevel === 3) {
      TemplateLevel(2); // Go back to Level 2
    } else if (currentTemplateLevel === 2) {
      TemplateLevel(1); // Go back to Level 1
    } 
  }
  
  return (
    <NewModal 
      modalState={isModalOpen}
      overrides={{ 
        mainContentPadding: MainContentPaddingTypes.secondary,
        headerContentHeight: "65px",
        secondaryContentScroll: true
      }}
      predefinedButtons={{
        onClose: () => { closeModal(); setReportData(cloneDeep(template))},
        onGoBack: currentTemplateLevel === 2 || currentTemplateLevel === 3? onGoBack : undefined
      }}
      header={t("reportPages.reports.newReport", { defaultValue: "new report" })}
      subHeader={template ? `${t("reportPages.reports.fromTemplate", {defaultValue: "from template"})} ${template.name}` : ""}
      headerContent={
        <ProgressSteps 
          titles={[t("reportPages.reports.dataLevel", {defaultValue: "data level"}), t("reportPages.reports.filterParameter", {defaultValue: "filter parameter"}), t("reportPages.reports.dataParameter", {defaultValue: "data parameter"})]} 
          icons={[glyphs.Animal({}), glyphs.Animal({}), glyphs.Animal({})]} opacityValues={currentTemplateLevel === 1 ? [1, 0.5, 0.5] : currentTemplateLevel === 2 ? [0.5, 1, 0.5] : [0.5, 0.5, 1] }
        />
      }
      secondaryContent={ currentTemplateLevel !== 1 ?
        <div style={{ width: mobileXs ? "290px" : "615px" }}>
          {currentTemplateLevel === 2 && <ReportModalLvl2 reportData={reportData} onReportDataChange={setReportData}/>}
          {currentTemplateLevel === 3 && <ReportModalLvl3 reportData={reportData} onReportDataChange={setReportData}/>}             
          {loading && <WaitAnimation color={colors.greyLightTertiary} />}
        </div> : undefined}
      mainContent={currentTemplateLevel === 1 ?
        <div style={{ minWidth: mobileXs ? "290px" : "615px" }}>
          <ReportModalLvl1 reportData={reportData} onReportDataChange={setReportData}/>
        </div> : undefined}
      footerContent={
        <NewButton 
          content={currentTemplateLevel === 1||currentTemplateLevel === 2 ? t("buttons.next", {defaultValue: "next"}) : t("reportPages.reports.createReport", {defaultValue: "create report"})}
          onClick={currentTemplateLevel === 1 ? () => TemplateLevel(2)
            : currentTemplateLevel === 2 ? () => TemplateLevel(3)
              : async () => {
                if (reportData && maintainReport) {
                  setLoading(true);
                  const affectedIds = await maintainReport(undefined, undefined, { data: { ...reportData, endDate: reportData.endDate ? reportData.endDate / 1000 : undefined, startDate: reportData.startDate ? reportData.startDate / 1000 : undefined } })
                  setLoading(false);
                  if (affectedIds && !(affectedIds[0] === undefined || affectedIds[0] === null)) {
                    RootNavigation({Navigator: navigator, navigation_page: Pages.reports, navigation_tab: Pages.report, replace: true, navArgs: { selected: affectedIds[0] }, currentDomain: farm});
                    closeModal();
                  } 
                };
              }}
        />
      }
    />
  );
}